/**
 * Convierte las preferencias en variables CSS sobre `<html>`.
 *
 * Se escriben con `style.setProperty` (no con un `<style>` ni un atributo en el HTML), así
 * funciona con la política de seguridad `style-src 'self'` del nginx. Los valores salen solo
 * de constantes del código o de hex validados.
 */
import { guardarPreferencia } from '../../lib/almacenamiento';
import {
  TEXTO_CLARO,
  asegurarContraste,
  esOscuro,
  mezclar,
  textoSobre,
  textoSobreVarios,
} from './color';
import { DEGRADADOS, PALETAS } from './paletas';
import type { PreferenciasTema } from './preferencias';

export interface TemaCalculado {
  /** `claro` / `oscuro` fuerza el tema; `null` deja que decida el sistema. */
  atributo: 'claro' | 'oscuro' | null;
  variables: Record<string, string>;
}

/** Todas las variables que el tema personalizado puede tocar (para limpiarlas al cambiar). */
export const VARIABLES = [
  '--fondo',
  '--fondo-imagen',
  '--superficie',
  '--texto',
  '--texto-suave',
  '--borde',
  '--gris-suave',
  '--campo',
  '--acento',
  '--acento-suave',
  '--acento-texto',
  '--texto-fondo',
  '--texto-suave-fondo',
  '--acento-fondo',
  '--esqueleto',
  '--esqueleto-brillo',
] as const;

/** Texto atenuado que igual cumple contraste de lectura (4,5) sobre `fondo`. */
function atenuar(texto: string, fondo: string): string {
  return asegurarContraste(mezclar(texto, fondo, 0.3), [fondo], texto);
}

export function calcularTema(preferencias: PreferenciasTema): TemaCalculado {
  const variables: Record<string, string> = {};
  let acentoActual: string | null = null;
  let atributo: TemaCalculado['atributo'] =
    preferencias.modo === 'sistema' ? null : preferencias.modo;

  const paleta = PALETAS.find((p) => p.id === preferencias.paleta);
  const degradado = DEGRADADOS.find((d) => d.id === preferencias.degradado);

  let fondoBase = paleta?.fondo;

  if (degradado) {
    variables['--fondo'] = degradado.desde;
    variables['--fondo-imagen'] = `linear-gradient(135deg, ${degradado.desde}, ${degradado.hasta})`;
  } else if (preferencias.fondoColor) {
    fondoBase = preferencias.fondoColor;
  }
  if (fondoBase && !degradado) variables['--fondo'] = fondoBase;

  if (paleta) {
    const { superficie } = paleta;
    const texto = textoSobre(superficie);
    const oscuro = esOscuro(superficie);
    atributo = oscuro ? 'oscuro' : 'claro';
    const acentoSuave = mezclar(superficie, paleta.acento, 0.16);
    // El acento se usa como color de texto sobre la superficie y sobre su versión suave.
    const columna = mezclar(superficie, texto, 0.07);
    const acento = asegurarContraste(paleta.acento, [superficie, acentoSuave, columna], texto);
    acentoActual = acento;
    Object.assign(variables, {
      '--superficie': superficie,
      '--texto': texto,
      '--texto-suave': atenuar(texto, superficie),
      '--borde': mezclar(superficie, texto, 0.2),
      '--gris-suave': mezclar(superficie, texto, 0.07),
      '--campo': mezclar(superficie, texto, 0.04),
      '--acento': acento,
      '--acento-suave': mezclar(superficie, acento, 0.16),
      '--acento-texto': textoSobre(acento),
      // Esqueletos de carga: un poco más marcados que la columna, y el brillo entre ambos.
      '--esqueleto': mezclar(superficie, texto, 0.1),
      '--esqueleto-brillo': mezclar(superficie, texto, 0.04),
    });
  }

  // Texto que va directo sobre el fondo (títulos, filtros): depende del fondo, no de la superficie.
  const fondos = degradado ? [degradado.desde, degradado.hasta] : fondoBase ? [fondoBase] : null;
  if (fondos) {
    const texto = textoSobreVarios(fondos);
    variables['--texto-fondo'] = texto;
    variables['--texto-suave-fondo'] = asegurarContraste(
      mezclar(texto, fondos[0] ?? texto, 0.3),
      fondos,
      texto,
    );
    // Enlaces y foco que quedan sobre el fondo: el acento de siempre puede perderse sobre él.
    const base = acentoActual ?? (textoSobreVarios(fondos) === TEXTO_CLARO ? '#5fc2a4' : '#1e6b57');
    variables['--acento-fondo'] = asegurarContraste(base, fondos, texto);
  }

  return { atributo, variables };
}

/** Aplica el tema, la densidad y el esfuerzo a `<html>`; si no hay personalización, deja todo como en `tokens.css`. */
export function aplicarTema(preferencias: PreferenciasTema, raiz = document.documentElement): void {
  const { atributo, variables } = calcularTema(preferencias);
  for (const nombre of VARIABLES) raiz.style.removeProperty(nombre);
  for (const [nombre, valor] of Object.entries(variables)) raiz.style.setProperty(nombre, valor);

  if (atributo) raiz.setAttribute('data-tema', atributo);
  else raiz.removeAttribute('data-tema');
  // Densidad y esfuerzo son atributos (no colores): el CSS decide qué cambia en cada uno.
  raiz.setAttribute('data-densidad', preferencias.densidad);
  raiz.setAttribute('data-esfuerzo', preferencias.esfuerzo);
  raiz.style.colorScheme = atributo === 'oscuro' ? 'dark' : atributo === 'claro' ? 'light' : '';
}

/** Clave donde se guarda el resultado ya calculado para `public/tema-inicial.js` (ver abajo). */
export const CLAVE_CACHE_TEMA = 'tema-css';

/**
 * Guarda el tema ya calculado. `public/tema-inicial.js` lo lee antes del primer pintado y solo lo
 * aplica (sin recalcular), para no duplicar aquí la lógica de contraste en un segundo lenguaje.
 */
export function guardarCacheTema(preferencias: PreferenciasTema): void {
  guardarPreferencia(CLAVE_CACHE_TEMA, JSON.stringify(calcularTema(preferencias)));
}
